<template>
  <div class="space-y-8">
    <!-- Header Section -->
    <div class="flex justify-between items-center">
      <div>
        <h3 class="text-lg font-medium text-heading">
          {{ $t('AI_SETTINGS.DATA_COLLECTION.TITLE') }}
        </h3>
        <p class="text-sm text-body-light">
          {{ $t('AI_SETTINGS.DATA_COLLECTION.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <!-- Main Configuration Section -->
    <div class="sm:rounded-xl sm:border sm:border-white/20 sm:dark:border-white/10 sm:bg-white/50 sm:dark:bg-slate-900/30 sm:backdrop-blur-md sm:shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.06)] sm:dark:shadow-[0_1px_2px_rgba(0,0,0,0.35),0_8px_24px_rgba(0,0,0,0.4)] sm:p-6">
      <div class="space-y-6">
        <!-- Enable Data Collection Toggle -->
        <div class="flex items-center justify-between">
          <div>
            <h4 class="text-base font-medium text-heading">
              {{ $t('AI_SETTINGS.DATA_COLLECTION.ENABLE_TOGGLE.TITLE') }}
            </h4>
            <p class="text-sm text-body-light">
              {{ $t('AI_SETTINGS.DATA_COLLECTION.ENABLE_TOGGLE.DESCRIPTION') }}
            </p>
          </div>
          <woot-switch
            :model-value="config.is_enabled"
            @input="onToggleEnabled"
          />
        </div>

        <!-- Auto Stop Chatbot Toggle -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-base font-medium text-heading">
                {{ $t('AI_SETTINGS.DATA_COLLECTION.AUTO_STOP_TOGGLE.TITLE') }}
              </h4>
              <p class="text-sm text-body-light">
                {{ $t('AI_SETTINGS.DATA_COLLECTION.AUTO_STOP_TOGGLE.DESCRIPTION') }}
              </p>
            </div>
            <woot-switch
              :model-value="config.auto_stop_chatbot"
              @input="onToggleAutoStop"
            />
        </div>
        <div
          class="flex flex-wrap gap-2"
          :class="{
            'pointer-events-none opacity-50': !config.auto_stop_chatbot
            }"
          >
            <woot-button
              v-for="option in autoStopOptions"
              :key="option.value"
              size="small"
              variant="smooth"
              class="transition-colors !px-3 !py-2 w-full sm:w-auto"
              :class="
                config.auto_stop_strategy === option.value
                  ? '!bg-grass-500 !text-white hover:!bg-grass-500'
                  : '!bg-slate-100 !text-slate-700 hover:!bg-slate-200 dark:!bg-slate-800 dark:hover:!bg-slate-700 dark:!text-slate-200'
              "
              :disabled="!config.auto_stop_chatbot"
              @click="setAutoStopStrategy(option.value)"
            >
              {{ $t(option.label) }}
            </woot-button>
          </div>
          <p
            v-if="config.auto_stop_chatbot && selectedAutoStopOption"
            class="text-xs text-body-light mt-1"
          >
            {{ $t(selectedAutoStopOption.description) }}
          </p>
        </div>

        <!-- Completion Label -->
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-base font-medium text-heading">
                {{ $t('AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.TITLE') }}
              </h4>
              <p class="text-sm text-body-light">
                {{ $t('AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.DESCRIPTION') }}
              </p>
            </div>
            <woot-switch
              :model-value="config.auto_add_label"
              @input="onToggleAutoAddLabel"
            />
          </div>
          <div class="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
            <woot-input
              v-model="config.completion_label_name"
              size="small"
              :placeholder="$t('AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.PLACEHOLDER')"
              :disabled="!config.auto_add_label"
              @blur="onCompletionLabelBlur"
            />
            <div class="flex items-center gap-3">
              <label class="text-sm text-body-light md:text-right md:w-32">
                {{ $t('AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.COLOR_LABEL') }}
              </label>
              <woot-color-picker
                v-model="config.completion_label_color"
                :disabled="!config.auto_add_label"
                @input="onCompletionLabelColorInput"
              />
            </div>
          </div>
          <p class="text-xs text-body-light">
            {{ $t('AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.HELP_TEXT') }}
          </p>
          <div
            class="flex flex-wrap gap-2"
            :class="{
              'pointer-events-none opacity-50': !config.auto_add_label
              }"
          >
            <woot-button
              v-for="option in autoAddLabelOptions"
              :key="option.value"
              size="small"
              variant="smooth"
              class="transition-colors !px-3 !py-2 w-full sm:w-auto"
              :class="
                config.auto_add_label_strategy === option.value
                  ? '!bg-primary-500 !text-white hover:!bg-primary-500'
                  : '!bg-slate-100 !text-slate-700 hover:!bg-slate-200 dark:!bg-slate-800 dark:hover:!bg-slate-700 dark:!text-slate-200'
              "
              :disabled="!config.auto_add_label"
              @click="setAutoAddLabelStrategy(option.value)"
            >
              {{ $t(option.label) }}
            </woot-button>
          </div>
          <p
            v-if="config.auto_add_label && selectedAutoAddLabelOption"
            class="text-xs text-body-light mt-1"
          >
            {{ $t(selectedAutoAddLabelOption.description) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Fields Management Section -->
    <div class="sm:rounded-xl sm:border sm:border-white/20 sm:dark:border-white/10 sm:bg-white/50 sm:dark:bg-slate-900/30 sm:backdrop-blur-md sm:shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.06)] sm:dark:shadow-[0_1px_2px_rgba(0,0,0,0.35),0_8px_24px_rgba(0,0,0,0.4)] sm:p-6">
      <div class="flex justify-between items-center mb-6">
        <h4 class="text-base font-medium text-heading">
          {{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.TITLE') }}
        </h4>
        <woot-button
          class="!bg-grass-500 !text-white"
          icon="add"
          size="small"
          @click="showFieldModal = true"
        >
          {{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.ADD_FIELD') }}
        </woot-button>
      </div>

      <!-- Fields List -->
      <div v-if="fields.length" class="space-y-4">
        <div
          v-for="field in fields"
          :key="field.id"
          class="flex items-center justify-between p-0 md:p-4 border border-white/15 dark:border-white/10 rounded-lg bg-white/70 dark:bg-slate-900/40"
        >
          <div class="flex-1">
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-2">
                <woot-switch
                  :model-value="field.is_enabled"
                  @input="val => { field.is_enabled = val; updateField(field) }"
                />
                <span class="text-sm font-medium text-heading">
                  {{ field.display_name }}
                </span>
              </div>
              <span class="text-xs text-body-light">({{ field.field_name }})</span>
              <span
                v-if="field.is_required"
                class="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 dark:bg-red-500/20 dark:text-red-200 rounded-full"
              >
                {{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.REQUIRED') }}
              </span>
            </div>
            <p v-if="field.description" class="text-sm text-body-light mt-1">
              {{ field.description }}
            </p>
          </div>
          
          <div class="flex items-center gap-2">
            <woot-button
              v-tooltip.top="$t('GENERAL.EDIT')"
              variant="smooth"
              size="tiny"
              color-scheme="secondary"
              class-names="grey-btn"
              icon="edit"
              @click="editField(field)"
            />
            <woot-button
              v-tooltip.top="$t('GENERAL.DELETE')"
              variant="smooth"
              size="tiny"
              color-scheme="secondary"
              class-names="grey-btn"
              icon="dismiss-circle"
              @click="deleteField(field)"
            />
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center py-8 text-body-light"
      >
        {{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.EMPTY_STATE') }}
      </div>
    </div>

    <!-- Data Sync Section (target dropdown + dynamic card) -->
    <DataSyncCard />

    <!-- Field Modal -->
    <FieldModal
      v-if="showFieldModal"
      :show="showFieldModal"
      :field="editingField"
      @close="closeFieldModal"
      @save="saveField"
    />
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables'
import FieldModal from './FieldModal.vue'
import DataSyncCard from './DataSyncCard.vue'

export default {
  name: 'DataCollectionTab',
  
  components: {
    FieldModal,
    DataSyncCard,
  },
  
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['refresh'],
  
  data() {
    return {
      dismissedBanner: this.loadDismissedBanner(),
      config: {
        is_enabled: false,
        auto_stop_chatbot: false,
        auto_stop_strategy: 'required_fields',
        auto_add_label: false,
        auto_add_label_strategy: 'required_fields',
        completion_label_name: '',
        completion_label_color: '#1f93ff'
      },
      fields: [],
      showFieldModal: false,
      editingField: null,
      isLocalLoading: false,
      autoStopOptions: [
        {
          value: 'required_fields',
          label: 'AI_SETTINGS.DATA_COLLECTION.AUTO_STOP_TOGGLE.OPTIONS.REQUIRED_ONLY_LABEL',
          description:
            'AI_SETTINGS.DATA_COLLECTION.AUTO_STOP_TOGGLE.OPTIONS.REQUIRED_ONLY_DESCRIPTION'
        },
        {
          value: 'all_fields',
          label: 'AI_SETTINGS.DATA_COLLECTION.AUTO_STOP_TOGGLE.OPTIONS.ALL_FIELDS_LABEL',
          description:
            'AI_SETTINGS.DATA_COLLECTION.AUTO_STOP_TOGGLE.OPTIONS.ALL_FIELDS_DESCRIPTION'
        }
      ],
      autoAddLabelOptions: [
        {
          value: 'required_fields',
          label: 'AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.OPTIONS.REQUIRED_ONLY_LABEL',
          description:
            'AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.OPTIONS.REQUIRED_ONLY_DESCRIPTION'
        },
        {
          value: 'all_fields',
          label: 'AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.OPTIONS.ALL_FIELDS_LABEL',
          description:
            'AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.OPTIONS.ALL_FIELDS_DESCRIPTION'
        }
      ]
    }
  },

  computed: {
    selectedAutoStopOption() {
      return this.autoStopOptions.find(
        option => option.value === this.config.auto_stop_strategy
      )
    },
    selectedAutoAddLabelOption() {
      return this.autoAddLabelOptions.find(
        option => option.value === this.config.auto_add_label_strategy
      )
    },
    defaultCompletionLabel() {
      return this.$t('AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.DEFAULT_LABEL')
    },
    defaultCompletionColor() {
      return this.$t('AI_SETTINGS.DATA_COLLECTION.AUTO_ADD_LABEL.DEFAULT_COLOR')
    }
  },
  
  mounted() {
    this.loadDataCollectionConfig()
  },
  
  methods: {
    loadDismissedBanner() {
      try {
        return localStorage.getItem('ai_data_collection_banner_dismissed') === 'true';
      } catch (e) {
        return false;
      }
    },
    
    dismissInfoBanner() {
      this.dismissedBanner = true;
      try {
        localStorage.setItem('ai_data_collection_banner_dismissed', 'true');
      } catch (e) {
        console.warn('Failed to save banner state:', e);
      }
    },
    
    async loadDataCollectionConfig() {
      try {
        this.isLocalLoading = true
        const accountId = this.$store.getters.getCurrentAccountId
        const response = await window.axios.get(`/api/v1/accounts/${accountId}/data_collection`)
        
        if (response.data) {
          this.config = {
            is_enabled: response.data.is_enabled || false,
            auto_stop_chatbot: response.data.auto_stop_chatbot || false,
            auto_stop_strategy: response.data.auto_stop_strategy || 'required_fields',
            auto_add_label: response.data.auto_add_label || false,
            auto_add_label_strategy: response.data.auto_add_label_strategy || 'required_fields',
            completion_label_name: response.data.completion_label_name || '',
            completion_label_color:
              response.data.completion_label_color || this.defaultCompletionColor
          }
          this.normalizeCompletionLabel()
          this.fields = response.data.fields || []
        }
      } catch (error) {
        console.error('Error loading data collection config:', error)
        if (error.response && error.response.status !== 404) {
          useAlert(
            this.$t('AI_SETTINGS.DATA_COLLECTION.ERRORS.LOAD_FAILED'),
            { type: 'error' }
          )
        }
      } finally {
        this.isLocalLoading = false
      }
    },
    
    async updateConfig() {
      try {
        const accountId = this.$store.getters.getCurrentAccountId
        await window.axios.put(`/api/v1/accounts/${accountId}/data_collection`, this.config)
        useAlert(
          this.$t('AI_SETTINGS.DATA_COLLECTION.SUCCESS.CONFIG_UPDATED'),
          { type: 'success' }
        )
      } catch (error) {
        console.error('Error updating config:', error)
        useAlert(
          this.$t('AI_SETTINGS.DATA_COLLECTION.ERRORS.CONFIG_UPDATE_FAILED'),
          { type: 'error' }
        )
        // Revert changes
        this.loadDataCollectionConfig()
      }
    },

    onToggleEnabled(val) {
      this.config.is_enabled = val
      this.updateConfig()
    },

    onToggleAutoStop(val) {
      this.config.auto_stop_chatbot = val
      this.updateConfig()
    },

    setAutoStopStrategy(strategy) {
      if (!this.config.auto_stop_chatbot || this.config.auto_stop_strategy === strategy) {
        return
      }

      this.config.auto_stop_strategy = strategy
      this.updateConfig()
    },

    setAutoAddLabelStrategy(strategy) {
      if (!this.config.auto_add_label || this.config.auto_add_label_strategy === strategy) {
        return
      }

      this.config.auto_add_label_strategy = strategy
      this.updateConfig()
    },

    onToggleAutoAddLabel(val) {
      this.config.auto_add_label = val
      this.normalizeCompletionLabel()
      this.updateConfig()
    },

    onCompletionLabelBlur() {
      if (!this.config.auto_add_label) {
        return
      }

      this.normalizeCompletionLabel()
      this.updateConfig()
    },
    
    onCompletionLabelColorInput(value) {
      if (!this.config.auto_add_label) {
        return
      }

      const normalized =
        this.formatLabelColorInput(value) || this.defaultCompletionColor

      if (normalized === this.config.completion_label_color) {
        return
      }

      this.config.completion_label_color = normalized
      this.updateConfig()
    },

    normalizeCompletionLabel() {
      if (!this.config.auto_add_label) {
        return
      }

      const formattedName = this.formatLabelInput(this.config.completion_label_name)
      this.config.completion_label_name = formattedName || this.defaultCompletionLabel

      const formattedColor = this.formatLabelColorInput(
        this.config.completion_label_color || this.defaultCompletionColor
      )
      this.config.completion_label_color = formattedColor || this.defaultCompletionColor
    },

    formatLabelInput(value) {
      if (!value) return ''

      let stripped = value.toString().normalize('NFD')
      try {
        stripped = stripped.replace(/\p{Diacritic}/gu, '')
      } catch (error) {
        stripped = stripped.normalize('NFC')
      }

      return stripped
        .toLowerCase()
        .replace(/[^a-z0-9\s_-]/g, ' ')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_+|_+$/g, '')
    },

    formatLabelColorInput(value) {
      if (!value) {
        return ''
      }

      let color = value.toString().trim().toLowerCase()
      color = color.startsWith('#') ? color : `#${color}`
      color = `#${color.slice(1).replace(/[^0-9a-f]/g, '')}`

      if (color.length == 4) {
        color = `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
      }

      if (color.length != 7) {
        return ''
      }

      return color
    },
    
    async updateField(field) {
      try {
        const accountId = this.$store.getters.getCurrentAccountId
        await window.axios.put(`/api/v1/accounts/${accountId}/data_collection/fields/${field.id}`, {
          field_name: field.field_name,
          display_name: field.display_name,
          description: field.description,
          is_required: field.is_required,
          is_enabled: field.is_enabled,
          order_index: field.order_index
        })
        useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.SUCCESS.FIELD_UPDATED'), {
          type: 'success',
        })
      } catch (error) {
        console.error('Error updating field:', error)
        useAlert(
          this.$t('AI_SETTINGS.DATA_COLLECTION.ERRORS.FIELD_UPDATE_FAILED'),
          { type: 'error' }
        )
        // Revert changes
        this.loadDataCollectionConfig()
      }
    },
    
    editField(field) {
      this.editingField = { ...field }
      this.showFieldModal = true
    },
    
    async deleteField(field) {
      if (!window.confirm(this.$t('AI_SETTINGS.DATA_COLLECTION.DELETE.CONFIRM_MESSAGE'))) return
      
      try {
        const accountId = this.$store.getters.getCurrentAccountId
        await window.axios.delete(`/api/v1/accounts/${accountId}/data_collection/fields/${field.id}`)
        
        useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.SUCCESS.FIELD_DELETED'), {
          type: 'success',
        })
        this.loadDataCollectionConfig()
      } catch (error) {
        console.error('Error deleting field:', error)
        useAlert(
          this.$t('AI_SETTINGS.DATA_COLLECTION.ERRORS.FIELD_DELETE_FAILED'),
          { type: 'error' }
        )
      }
    },
    
    closeFieldModal() {
      this.showFieldModal = false
      this.editingField = null
    },
    
    async saveField(fieldData) {
      try {
        const accountId = this.$store.getters.getCurrentAccountId
        
        if (this.editingField) {
          // Update existing field
          await window.axios.put(`/api/v1/accounts/${accountId}/data_collection/fields/${this.editingField.id}`, fieldData)
          useAlert(
            this.$t('AI_SETTINGS.DATA_COLLECTION.SUCCESS.FIELD_UPDATED'),
            { type: 'success' }
          )
        } else {
          // Create new field
          await window.axios.post(`/api/v1/accounts/${accountId}/data_collection/fields`, fieldData)
          useAlert(
            this.$t('AI_SETTINGS.DATA_COLLECTION.SUCCESS.FIELD_CREATED'),
            { type: 'success' }
          )
        }
        
        this.closeFieldModal()
        this.loadDataCollectionConfig()
      } catch (error) {
        console.error('Error saving field:', error)
        useAlert(
          this.$t('AI_SETTINGS.DATA_COLLECTION.ERRORS.FIELD_SAVE_FAILED'),
          { type: 'error' }
        )
      }
    }
  }
}
</script>
